<template>
  <v-card>
    <v-toolbar flat color="white" density="compact">
      <v-text-field
          density="compact"
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          color="primary"
          v-model="search"
          hide-details
          single-line
          flat
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('search') }}</span>
        </template>
      </v-text-field>
      <v-autocomplete
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.root_asset_name"
          :items="advanced_search_models.root_asset_name"
          @update:model-value="showRecords"
          :return-object="false"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('root_asset_name') }}</span>
        </template>
      </v-autocomplete>
      <v-autocomplete
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.cluster"
          :items="advanced_search_models.cluster"
          @update:model-value="showRecords"
          :return-object="false"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('cluster') }}</span>
        </template>
      </v-autocomplete>
      <v-autocomplete
          class="reg-normal text-primary ml-5"
          base-color="border-tertiary"
          bg-color="secondary"
          density="compact"
          v-model="advanced_search.priority"
          :items="[{ value: 1, text: 'Priority 1'}, { value: 2, text: 'Priority 2'}, { value: 3, text: 'Priority 3'}]"
          @update:model-value="showRecords"
          :return-object="false"
          item-value="value"
          item-title="text"
          hide-details
          single-line
          clearable>
        <template #label>
          <span class="sm-normal text-subtle">{{ $store.getters.translate('priority') }}</span>
        </template>
      </v-autocomplete>
      <v-spacer/>
      <v-menu>
        <template #activator="{ props }">
          <v-btn elevation="0" icon v-bind="props">
            <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-if="getClosedState" @click="setClosedState(false)">
            <template #prepend><PhEye size="24"/></template>
            <v-list-item-title class="sm-normal link ml-2">
              <span>{{ $store.getters.translate('show_closed') }}</span>
            </v-list-item-title>
          </v-list-item>
          <v-list-item v-else @click="setClosedState(true)">
            <template #prepend><PhEyeSlash size="24"/></template>
            <v-list-item-title class="sm-normal link ml-2">
              <span>{{ $store.getters.translate('hide_closed') }}</span>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar>
    <div style="overflow-x: auto; width: 100%;">
      <v-data-table
          :headers="headers"
          :items="getRecords"
          class="elevation-1"
          @click:row="handleClick"
          :search="search"
          v-bind="footer_props"
          v-model:items-per-page="options.itemsPerPage"
          v-model:page="options.page"
          :show-select="false"
          :group-by="[{key: 'article_id', order: 'asc'}]">
        <template #group-header="{item, columns, toggleGroup, isGroupOpen}">
          <tr>
            <td :colspan="columns.length">
              <v-btn size="small" variant="flat" @click="toggleGroup(item)" :ref="'group-' + item.value" :data-open="isGroupOpen(item)">
                <v-icon :icon="isGroupOpen(item) ? '$expand' : '$next'" size="x-large"/>
                {{ $store.getters.translate("equipment") + " ID: " + item.value }}
              </v-btn>
            </td>
          </tr>
        </template>
        <template #item.status="{ item }">
          <template v-if="item.opened">
            <v-icon color="red" size="8" class="ml-2 mr-2">mdi-circle</v-icon>
            <span class="sm-normal">Not Ready</span>
          </template>
          <template v-else>
            <v-icon color="green" size="8" class="ml-2 mr-2">mdi-circle</v-icon>
            <span class="sm-normal">Ready</span>
          </template>
        </template>
        <template #item.priority="{ item }">
          <PhNumberCircleZero v-if="item.priority === 0" color="red" size="24"/>
          <PhNumberCircleOne v-else-if="item.priority === 1" color="red" size="24"/>
          <PhNumberCircleTwo v-else-if="item.priority === 2" color="orange" size="24"/>
          <PhNumberCircleThree v-else-if="item.priority === 3" color="orange" size="24"/>
          <span v-else>Not set</span>
        </template>
        <template #item.distribution_device_function_name="{ item }">
          {{ $store.getters.translate(item.distribution_device_function_name) }}
        </template>
        <template #item.deficiency_group="{ item }">
          {{ $store.getters.translate(item.deficiency_group) }}
        </template>
        <template #item.drawing_number="{ item }">
          <div>{{ item.drawing_number + ', ' }}</div>
          <div v-if="item.page">{{ $store.getters.translate('page') + ': ' + item.page }}</div>
        </template>
        <template #item.defect_description="{ item }">
          <v-menu :offset="[10, 0]" location="right bottom" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon size="small" variant="text" color="primary">
                <PhChatTeardropText size="24" />
              </v-btn>
            </template>
            <v-card max-width="400" min-width="300" elevation="1" color="greens-100" border="sm" class="sm-normal">
              <v-card-text class="pa-2" style="max-height: 200px; overflow-y: auto;">
                <div style="white-space: pre-wrap; word-break: break-word;">
                  {{ $lodash.truncate(item.defect_description, { length: 600 }) }}
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
        </template>
        <template #item.remarks="{ item }">
          <v-menu :offset="[10, 0]" location="right bottom" :close-on-content-click="false">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon size="small" variant="text" color="primary">
                <PhChatTeardropText size="24" />
              </v-btn>
            </template>
            <v-card max-width="400" min-width="300" elevation="1" color="greens-100" border="sm" class="sm-normal">
              <v-card-text class="pa-2" style="max-height: 200px; overflow-y: auto;">
                <div style="white-space: pre-wrap; word-break: break-word;">
                  {{ $lodash.truncate(item.remarks, { length: 600 }) }}
                </div>
              </v-card-text>
            </v-card>
          </v-menu>
        </template>
        <template #item.actions="{ item }">
          <v-menu>
            <template #activator="{ props }">
              <v-btn elevation="0" icon v-bind="props">
                <PhDotsThreeVertical size="24" style="cursor: pointer;"/>
              </v-btn>
            </template>
            <v-list>
              <v-list-item v-if="canEdit" @click="$emit('edit', item)">
                <template #prepend><PhPencilSimpleLine size="24"/></template>
                <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('edit') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canDelete" @click="deleteRecord(item.id)">
                <template #prepend><PhTrash size="24"/></template>
                <v-list-item-title class="sm-normal link ml-2">{{ $store.getters.translate('delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </div>
  </v-card>
</template>

<script>
import {useAbility} from "@casl/vue";
import helpFunctions from "../../plugins/helpFunctions";
import { PhChatTeardropText, PhDotsThreeVertical, PhEye, PhEyeSlash, PhNumberCircleOne, PhNumberCircleThree, PhNumberCircleTwo, PhNumberCircleZero, PhPencilSimpleLine, PhTrash } from "@phosphor-icons/vue";

export default {
  components: {
    PhTrash, PhPencilSimpleLine, PhChatTeardropText, PhNumberCircleZero, PhEyeSlash, PhEye, PhDotsThreeVertical, PhNumberCircleThree, PhNumberCircleTwo, PhNumberCircleOne,
  },
  setup() {
    const { can } = useAbility()
    return { can }
  },
  data() {
    return {
      page: helpFunctions.pages.defects,
      headers: helpFunctions.headers.defects,
      all_records: [],
      records: [],
      options: {},
      footer_props: helpFunctions.footer_props,
      search: '',
      advanced_search: {
        root_asset_name: null,
        cluster: null,
        priority: null,
      },
      advanced_search_models: {
        root_asset_name: [],
        cluster: [],
      },
    };
  },
  created() {
    if(helpFunctions.getLocalStorageValue(this.page.name + "_options")) {
      this.options = helpFunctions.getLocalStorageValue(this.page.name + "_options");
      this.options.hide_closed_defects = true;
    }
    else {
      this.options = helpFunctions.table_options;
    }
  },
  methods: {
    fillAdvancedSearchArrays() {
      this.all_records.forEach(record => {
        if(record.root_asset_name && !this.advanced_search_models.root_asset_name.includes(record.root_asset_name)) {
          this.advanced_search_models.root_asset_name.push(record.root_asset_name);
        }
        if(record.cluster && !this.advanced_search_models.cluster.includes(record.cluster)) {
          this.advanced_search_models.cluster.push(record.cluster);
        }
      });
      this.showRecords();
    },
    showRecords() {
      let sorted_array = this.all_records;
      if(this.options.hide_closed_defects) {
        sorted_array = sorted_array.filter((item) => item.opened);
      }
      if(this.advanced_search.root_asset_name) {
        sorted_array = sorted_array.filter(item => item.root_asset_name === this.advanced_search.root_asset_name);
      }
      if(this.advanced_search.cluster) {
        sorted_array = sorted_array.filter(item => item.cluster === this.advanced_search.cluster);
      }
      if(this.advanced_search.priority) {
        sorted_array = sorted_array.filter(item => item.priority === this.advanced_search.priority);
      }
      this.records = sorted_array;
      this.openAll();
    },
    handleClick(event, row) {
      this.$emit('edit', row.item);
    },
    setClosedState(new_state) {
      this.options.hide_closed_defects = new_state;
    },
    async deleteRecord(id) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          await helpFunctions.deleteModel(this.$toast, this.page.name, id);
          this.$emit('refresh');
        }
      });
    },
    openAll() {
      this.$nextTick(() => {
        Object.keys(this.$refs).forEach(k => {
          if (k.startsWith('group-') && this.$refs[k] && !this.$refs[k].$attrs['data-open']) {
            this.$refs[k].$el.click();
          }
        });
      });
    },
  },
  computed: {
    getClosedState() {
      return this.options.hide_closed_defects;
    },
    getRecords() {
      if(this.records) {
        return this.records;
      }
      else {
        return [];
      }
    },
    canEdit() {
      return this.can("edit", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
    canDelete() {
      return this.can("delete", this.$createEntity(this.page.model.toLowerCase(), { id: 0 }));
    },
  },
  watch: {
    options: {
      handler() {
        helpFunctions.setLocalStorageValue(this.page.name + "_options", this.options);
        this.showRecords();
      },
      deep: true,
    },
    advanced_search: {
      handler() {
        this.options.page = 1;
      },
      deep: true,
    },
  },
};
</script>